import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth } from '../../firebase'
import Button from '../Button'
import { verifyTotp } from '../../hooks/api'
import Spinner from '../Spinner'
import useGetUserName from '../../hooks/useGetUserData'
import { toast } from 'react-hot-toast' // ✅ Toast import

export default function OtpVerificationScreen() {
  const navigate = useNavigate()
  const userId = auth.currentUser?.uid
  const { user: profile, loading, error, refetch } = useGetUserName(userId)

  const [otp, setOtp] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [hasRetried, setHasRetried] = useState(false)

  useEffect(() => {
    if (error && !hasRetried) {
      const timer = setTimeout(() => {
        refetch() // retry once
        setHasRetried(true)
      }, 1000) // 1 second delay before retry
      return () => clearTimeout(timer)
    }
  }, [error, hasRetried, refetch])

  useEffect(() => {
    if (!loading) setIsChecking(false)
  }, [loading])

  const verifyMfa = async () => {
    setIsLoading(true)

    try {
      if (!profile) {
        toast.error('User data not loaded', { id: 'user-notfound' })
        return
      }

      // The server checks the code and writes this environment's `mfa` claim,
      // then the token is refreshed. Checking it here left the result as a
      // boolean in browser memory, which is Appendix A 1.4. Recovery codes go
      // the same way, and the server marks them used.
      await verifyTotp({
        code: useRecovery ? recoveryCode : otp,
        isRecoveryCode: useRecovery
      })

      // ✅ Success toast
      toast.success('Two-factor verification successful!')

      // Clear MFA pending state
      localStorage.removeItem('mfaInProgress')
      localStorage.removeItem('mfaUserId')

      // Navigate to dispatch/dashboard
      navigate('/dispatch')
    } catch (err) {
      console.error('Verification error:', err)
      toast.error(err?.message || 'Verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = async () => {
    await signOut(auth)
    localStorage.removeItem('mfaInProgress')
    localStorage.removeItem('mfaUserId')
    navigate('/login')
    toast('Authentication cancelled', { icon: 'ℹ️' }) // ✅ Info toast
  }

  const handleResetMfa = () => {
    navigate('/reset-authentication', { state: { userData: profile } })
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return
    const newOtp = otp.split('')
    newOtp[index] = value
    setOtp(newOtp.join(''))
    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`)?.focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus()
    }
  }

  if (loading || (!profile && !hasRetried)) {
    return (
      <div className='login-container'>
        <div className='login-grid'>
          <div className='login-left login-centered'>
            <div className='login-form'>
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !profile) return <div>Error loading user data.</div>

  return (
    <div className='login-container'>
      <div className='login-grid'>
        <div className='login-left login-centered'>
          <div className='login-form'>
            <h2 className='!text-xl font-bold mb-4'>
              Two-Factor Authentication
            </h2>

            {!useRecovery ? (
              <>
                <p className='mb-4'>
                  Enter the 6-digit code from your authenticator app:
                </p>
                <div className='flex gap-2 mb-4 justify-start'>
                  {[...Array(6)].map((_, index) => (
                    <input
                      key={index}
                      id={`otp-input-${index}`}
                      type='text'
                      value={otp[index] || ''}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className='w-10 h-10 text-center text-lg border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
                      maxLength={1}
                      inputMode='numeric'
                      pattern='\d*'
                    />
                  ))}
                </div>
                <div className='flex items-center mb-4'>
                  <button
                    type='button'
                    onClick={() => setUseRecovery(true)}
                    className='text-blue-500 hover:underline'>
                    Use recovery code instead
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className='mb-4'>Enter one of your recovery codes:</p>
                <input
                  type='text'
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  className='w-full p-2 border rounded mb-4'
                  placeholder='ABCDEF'
                />
                <div className='flex items-center mb-4'>
                  <button
                    type='button'
                    onClick={() => setUseRecovery(false)}
                    className='text-blue-500 hover:underline'>
                    Use authenticator app instead
                  </button>
                </div>
              </>
            )}

            <div className='flex items-center gap-4'>
              <Button
                onClick={handleCancel}
                className='login-button !bg-gray-500 !text-white'>
                Cancel
              </Button>
              <Button
                onClick={verifyMfa}
                className='login-button'
                disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>

            <div className='p-4 flex justify-center items-center cursor-pointer'>
              <div className='text-[#293894]' onClick={handleResetMfa}>
                <h5>Reset Authentication</h5>
              </div>
            </div>
          </div>
        </div>

        <div className='login-right login-centered login-overflow'>
          <img
            className='login-image'
            src='/assets/images/logo.png'
            alt='login visual'
          />
        </div>
      </div>
    </div>
  )
}
